import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaLinkedin,
  FaEnvelope,
  FaArrowUp,
  FaInfoCircle,
} from 'react-icons/fa';
import { HiMenu, HiX } from 'react-icons/hi';
import logo from '../assets/Logo-Blue-white.webp';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ThemeToggle from './ThemeToggle/ThemeToggle.jsx';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher.jsx';

// Proper names / link stay here. All visible wording lives in the locale
// files under `nav.partner.*` (en.json / de.json).
const PARTNER = {
  name: 'Innovestigia',
  code: 'IVG',
  url: 'https://innovestigia.com/',
};

const Navbar = ({ onActiveTabChange }) => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('HeroSection');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPartnerInfo, setShowPartnerInfo] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);

      if (window.innerWidth >= 768) {
        setIsOpen(false);
      }
    };

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        setShowScrollTop(window.pageYOffset > 300);

        const sections = [
          'HeroSection',
          'AboutSection',
          'DesignServices',
          'WhySection',
          'ContactUs',
        ];

        for (const section of sections) {
          const element = document.getElementById(section);

          if (element) {
            const rect = element.getBoundingClientRect();

            if (rect.top <= 100 && rect.bottom >= 100) {
              setActiveSection(section);
              break;
            }
          }
        }

        ticking = false;
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollTo = (id, isService) => {
    if (isService) {
      onActiveTabChange(id);

      const element = document.getElementById('all');

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        setActiveSection(id);
        setIsOpen(false);
      }
    } else {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        setActiveSection(id);
        setIsOpen(false);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    setActiveSection('HeroSection');
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isExtraSmall = windowWidth < 400;

  const navItems = [
    {
      id: 'AboutSection',
      label: t('nav.weAre'),
    },
    {
      id: 'DesignServices',
      label: t('nav.weDeliver'),
    },
    {
      id: 'WhySection',
      label: t('nav.whyVizcorn'),
    },
    {
      id: 'ContactUs',
      label: t('nav.buildTogether'),
    },
  ];

  const NavLink = ({ item, mobile }) => {
    const isActive = activeSection === item.id;

    return (
      <motion.button
        onClick={() =>
          mobile
            ? (scrollTo(item.id), setIsOpen(false))
            : scrollTo(item.id)
        }
        className={`relative font-semibold text-sm transition-colors duration-200 ${
          mobile
            ? `text-left px-4 py-3 rounded-lg ${
                isActive
                  ? 'text-white'
                  : 'text-nav-text hover:text-white hover:bg-white/5'
              }`
            : `px-1 py-2 ${
                isActive
                  ? 'text-nav-text-active'
                  : 'text-nav-text hover:text-white'
              }`
        }`}
        whileTap={{ scale: 0.96 }}
      >
        {item.label}

        {!mobile && (
          <span
            className={`absolute left-0 right-0 -bottom-1 h-[2px] rounded-full bg-vizcorn-blue transition-all duration-200 ${
              isActive
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        )}
      </motion.button>
    );
  };

  /*
   * TECHNOLOGY PARTNER BUTTON
   *
   * Desktop:
   * - White background
   * - Black text
   * - Hover shows light information card
   *
   * Click:
   * - Opens Innovestigia in a new tab
   */
  const PartnerBadge = ({ mobile }) => (
    <div
      className="relative"
      onMouseEnter={() => !mobile && setShowPartnerInfo(true)}
      onMouseLeave={() => !mobile && setShowPartnerInfo(false)}
    >
      <motion.a
        href={PARTNER.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('nav.partner.ariaLabel', { name: PARTNER.name })}
        whileTap={{ scale: 0.97 }}
        className={`
          relative
          inline-flex
          items-center
          justify-center
          gap-2
          rounded-xl
          bg-white
          text-black
          text-sm
          font-semibold
          border
          border-white/20
          shadow-[0_6px_18px_rgba(255,255,255,0.08)]
          transition-all
          duration-300
          hover:-translate-y-0.5
          hover:bg-gray-100
          hover:shadow-[0_10px_26px_rgba(255,255,255,0.16)]
          whitespace-nowrap
          ${mobile ? 'w-full px-4 py-3' : 'px-3.5 py-2'}
        `}
      >
        <span>{t('nav.partner.button')}</span>

        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-black opacity-40" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-black" />
        </span>
      </motion.a>

      {/* Desktop Hover Card */}
      {!mobile && (
        <AnimatePresence>
          {showPartnerInfo && (
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
                scale: 0.97,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: 8,
                scale: 0.97,
              }}
              transition={{
                duration: 0.2,
              }}
              className="
                absolute
                right-0
                top-[calc(100%+14px)]
                w-[330px]
                z-50
              "
            >
              {/* Small arrow */}
              <div
                className="
                  absolute
                  -top-2
                  right-8
                  w-4
                  h-4
                  rotate-45
                  bg-white
                  border-l
                  border-t
                  border-gray-200
                "
              />

              <div
                className="
                  relative
                  rounded-2xl
                  border
                  border-gray-200
                  bg-white
                  shadow-[0_20px_50px_rgba(0,0,0,0.15)]
                  p-5
                "
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-gray-100
                      text-gray-800
                    "
                  >
                    <FaInfoCircle className="text-lg" />
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      {t('nav.partner.about')}
                    </p>

                    <h3 className="mt-0.5 text-base font-semibold text-gray-900">
                      Vizcorn
                    </h3>
                  </div>
                </div>

                {/* Partner */}
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    rounded-xl
                    bg-gray-50
                    border
                    border-gray-200
                    px-4
                    py-3
                    mb-4
                  "
                >
                  <div>
                    <p className="text-xs text-gray-500">
                      {t('nav.partner.partnerLabel')}
                    </p>

                    <p className="text-sm font-semibold text-gray-900">
                      {PARTNER.name}
                    </p>
                  </div>

                  <span
                    className="
                      rounded-lg
                      bg-gray-900
                      px-2.5
                      py-1
                      text-xs
                      font-bold
                      text-white
                    "
                  >
                    {PARTNER.code}
                  </span>
                </div>

                {/* Description */}
                <p className="text-sm leading-6 text-gray-600">
                  {t('nav.partner.description')}
                </p>

                {/* Bottom info */}
                <div
                  className="
                    mt-4
                    flex
                    items-start
                    gap-2.5
                    border-t
                    border-gray-200
                    pt-4
                  "
                >
                  <FaInfoCircle
                    className="
                      mt-0.5
                      shrink-0
                      text-xs
                      text-gray-500
                    "
                  />

                  <p className="text-xs leading-5 text-gray-500">
                    {t('nav.partner.note')}
                  </p>
                </div>

                {/* Open link */}
                <div className="mt-4">
                  <span
                    className="
                      inline-flex
                      items-center
                      gap-2
                      text-xs
                      font-semibold
                      text-gray-900
                    "
                  >
                    {t('nav.partner.open')}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );

  return (
    <>
      <nav
        className="
          bg-black/80
          backdrop-blur-lg
          border-b
          border-nav-border
          px-4
          sm:px-6
          py-3
          fixed
          w-full
          top-0
          z-20
        "
      >
        <div
          className="
            max-w-7xl
            mx-auto
            flex
            items-center
            justify-between
            gap-4
          "
        >
          {/* Logo */}
          <div className="w-auto flex-shrink-0">
            <Link
              to={`/${lang}`}
              className="flex items-center gap-2"
            >
              <img
                src={logo}
                alt="Vizcorn"
                width={170}
                height={52}
                fetchPriority="high"
                className="
                  w-[150px]
                  sm:w-[170px]
                  h-auto
                  object-contain
                  cursor-pointer
                  hover:opacity-80
                  transition-opacity
                "
              />
            </Link>
          </div>

          {/* Center Navigation + Technology Partner */}
          {!isMobile && (
            <div
              className={`flex items-center ${
                isTablet ? 'gap-5' : 'gap-8'
              }`}
            >
              {navItems.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                />
              ))}

              {/* Technology Partner */}
              <PartnerBadge />
            </div>
          )}

          {/* Right side */}
          {!isMobile && (
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Email */}
              <a
                href="mailto:contact@vizcorn.com"
                className="
                  w-9
                  h-9
                  flex
                  items-center
                  justify-center
                  rounded-full
                  bg-nav-surface
                  text-nav-text
                  hover:text-white
                  hover:bg-white/10
                  transition-colors
                "
                aria-label="Email"
              >
                <FaEnvelope className="text-sm" />
              </a>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/vizcornkft/"
                target="_blank"
                rel="noopener noreferrer"
                className="
                  w-9
                  h-9
                  flex
                  items-center
                  justify-center
                  rounded-full
                  bg-nav-surface
                  text-nav-text
                  hover:text-white
                  hover:bg-white/10
                  transition-colors
                "
                aria-label="LinkedIn"
              >
                <FaLinkedin className="text-sm" />
              </a>

              <LanguageSwitcher />

              <ThemeToggle />

              <span className="w-px h-6 bg-nav-border mx-1" />

              {/* Let's Talk */}
              <motion.button
                onClick={() => scrollTo('ContactUs')}
                className="
                  inline-flex
                  items-center
                  gap-2
                  bg-vizcorn-blue
                  hover:bg-vizcorn-blue-dark
                  text-white
                  text-sm
                  font-semibold
                  px-4
                  py-2
                  rounded-lg
                  transition-colors
                "
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {t('nav.letsTalk')}
              </motion.button>
            </div>
          )}

          {/* Mobile */}
          {isMobile && (
            <div className="flex items-center gap-2">
              {!isExtraSmall && <ThemeToggle />}

              <button
                onClick={toggleMenu}
                className="
                  w-9
                  h-9
                  flex
                  items-center
                  justify-center
                  rounded-full
                  bg-nav-surface
                  text-nav-text
                  hover:text-white
                  transition-colors
                "
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <HiX className="text-lg" />
                ) : (
                  <HiMenu className="text-lg" />
                )}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="
                fixed
                inset-0
                bg-black
                z-30
                md:hidden
              "
              onClick={() => setIsOpen(false)}
            />

            {/* Mobile Drawer */}
            <motion.div
              key="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              className="
                fixed
                top-0
                right-0
                w-72
                max-w-[85vw]
                h-full
                bg-nav-bg/95
                backdrop-blur-xl
                border-l
                border-nav-border
                shadow-lg
                px-6
                py-10
                z-40
                flex
                flex-col
                gap-4
                md:hidden
              "
            >
              {/* Close */}
              <button
                onClick={() => setIsOpen(false)}
                className="
                  absolute
                  top-4
                  right-4
                  p-2
                  text-nav-text
                  hover:text-white
                "
                aria-label="Close menu"
              >
                <HiX className="text-xl" />
              </button>

              {/* Navigation */}
              {navItems.map((item) => (
                <NavLink
                  key={`mobile-${item.id}`}
                  item={item}
                  mobile
                />
              ))}

              {/* Social + Theme */}
              <div
                className="
                  flex
                  items-center
                  gap-3
                  pt-6
                  mt-6
                  border-t
                  border-nav-border
                "
              >
                <a
                  href="mailto:contact@vizcorn.com"
                  className="
                    w-9
                    h-9
                    flex
                    items-center
                    justify-center
                    rounded-full
                    bg-nav-surface
                    text-nav-text
                    hover:text-white
                    transition-colors
                  "
                  aria-label="Email"
                >
                  <FaEnvelope className="text-sm" />
                </a>

                <a
                  href="https://www.linkedin.com/company/vizcornkft/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    w-9
                    h-9
                    flex
                    items-center
                    justify-center
                    rounded-full
                    bg-nav-surface
                    text-nav-text
                    hover:text-white
                    transition-colors
                  "
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="text-sm" />
                </a>

                <LanguageSwitcher />

                <ThemeToggle />
              </div>

              {/* Technology Partner */}
              <PartnerBadge mobile />

              {/* Let's Talk */}
              <button
                onClick={() => {
                  scrollTo('ContactUs');
                  setIsOpen(false);
                }}
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  bg-vizcorn-blue
                  hover:bg-vizcorn-blue-dark
                  text-white
                  text-sm
                  font-semibold
                  px-4
                  py-3
                  rounded-lg
                  transition-colors
                  mt-2
                "
              >
                {t('nav.letsTalk')}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Scroll to top button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            className="
              fixed
              bottom-6
              right-6
              z-30
              w-10
              h-10
              flex
              items-center
              justify-center
              rounded-full
              bg-vizcorn-blue
              text-white
              shadow-lg
              hover:bg-vizcorn-blue-dark
              transition-colors
            "
            aria-label="Scroll to top"
          >
            <FaArrowUp className="text-sm" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;